import React, {useEffect, useState} from "react";
import "./style.css";
import SwitchSelect from "../../../../components/SwitchSelect";
import WeightModel from "../../../../domin/questionnaire/weight-loss/Weight";
import {TargetWeightResult} from "./TargetWeightResult/TargetWeightResult";
import { convertWeight, getWeightUnitFromHeight } from "../../../../utils/common";
import { FloatingContinueBtn } from "../../../../components/FloatingContinueBtn";

export const TargetWeight = ({clickNext, data, clicked, setAllow, isCurPage, onPageFocus, onPageKeyDown, inputRef}) => {
  // 从height单位推导weight单位：ft -> lbs, cm -> kg
  const getWeightUnit = () => {
    const heightUnit = data?.height?.unit;
    return getWeightUnitFromHeight(heightUnit);
  };
  
  const [unit, setUnit] = useState(data?.targetWeight?.unit || getWeightUnit());
  const [weight, setWeight] = useState(data?.targetWeight?.weight || '');
  // 保存精确的重量小数值用于计算，不用于显示
  const [weightPrecise, setWeightPrecise] = useState(data?.targetWeight?.weight || '');
  
  // 控制浮动按钮显示
  const [showFloatingBtn, setShowFloatingBtn] = useState(false);
  
  // 验证重量是否在有效范围内
  const isWeightValid = (weightValue, unitValue) => {
    const w = Number(weightValue);
    if (Number.isNaN(w)) return false;
    
    if (unitValue === 'lbs') {
      return w >= 55 && w <= 662;
    } else { // kg
      return w >= 25 && w <= 300;
    }
  };

  // 验证并限制重量值范围（只限制最大值，最小值在blur时处理）
  const validateWeight = (value, unit) => {
    if (value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    
    // 只限制最大值
    if (unit === 'lbs') {
      if (num > 662) return '662';
    } else { // kg
      if (num > 300) return '300';
    }
    return value;
  };

  // 处理输入变化（只允许整数）
  const handleWeightChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    const validated = validateWeight(value, unit);
    setWeight(validated);
    setWeightPrecise(validated);
  };
  
  // 处理失去焦点：自动修正到最小值
  const handleWeightBlur = () => {
    if (weight !== '') {
      const num = parseInt(weight);
      if (!isNaN(num)) {
        if (unit === 'lbs' && num < 55) {
          setWeight('55');
          setWeightPrecise('55');
        } else if (unit === 'kg' && num < 25) {
          setWeight('25');
          setWeightPrecise('25');
        }
      }
    }
  };

  useEffect(() => {
    setAllow(isWeightValid(weight, unit));
  }, [weight, unit]);
  
  // 处理输入框聚焦
  const handleFocus = (e) => {
    setShowFloatingBtn(true);
    if (onPageFocus) onPageFocus(e);
  };
  
  // 处理输入框失焦（包含最小值修正）
  const handleWeightBlurWithFloat = () => {
    setTimeout(() => {
      setShowFloatingBtn(false);
    }, 100);
    handleWeightBlur();
  };
  
  // 处理浮动按钮点击
  const handleFloatingBtnClick = () => {
    if (isWeightValid(weight, unit)) {
      // 隐藏键盘
      if (inputRef.current) inputRef.current.blur();
      setShowFloatingBtn(false);
      // 触发提交
      clickNext(new WeightModel(weight, unit));
    }
  };

  useEffect(() => {
    if (clicked && isWeightValid(weight, unit)) {
      clickNext(new WeightModel(weight, unit));
    }
  }, [clicked]);

  // 监听height单位变化，同步更新weight单位并转换数据
  useEffect(() => {
    const newUnit = getWeightUnit();
    if (newUnit !== unit) {
      const oldUnit = unit;
      setUnit(newUnit);
      
      // 使用精确值进行转换
      if (weight) {
        const preciseValue = weightPrecise || weight;
        const convertedDecimal = parseFloat(convertWeight(preciseValue, oldUnit, newUnit));
        const convertedDisplay = Math.round(convertedDecimal);
        
        // 应用范围限制
        let finalDisplay = convertedDisplay;
        let finalPrecise = convertedDecimal;
        
        if (newUnit === 'lbs') {
          if (convertedDisplay < 55) {
            finalDisplay = 55;
            finalPrecise = 55;
          } else if (convertedDisplay > 662) {
            finalDisplay = 662;
            finalPrecise = 662;
          }
        } else { // kg
          if (convertedDisplay < 25) {
            finalDisplay = 25;
            finalPrecise = 25;
          } else if (convertedDisplay > 300) {
            finalDisplay = 300;
            finalPrecise = 300;
          }
        }
        
        setWeight(finalDisplay.toString());
        setWeightPrecise(finalPrecise.toString());
      }
    }
  }, [data?.height?.unit]);

  return (
    <div className="target-weight" data-model-id="16755:1075">
      <FloatingContinueBtn 
        show={showFloatingBtn} 
        allow={isWeightValid(weight, unit)}
        onClick={handleFloatingBtnClick}
      />
      <div className="frame-4">
        <div className="title">
          <div className="heading">What's your target weight?</div>
        </div>

        <div className="frame-5">
          <SwitchSelect data={["lbs", "kg"]} onClick={(newUnit) => {
            const oldUnit = unit;
            setUnit(newUnit);
            
            // 使用精确值进行转换
            if (weight) {
              const preciseValue = weightPrecise || weight;
              const convertedDecimal = parseFloat(convertWeight(preciseValue, oldUnit, newUnit));
              const convertedDisplay = Math.round(convertedDecimal);
              
              // 应用范围限制
              let finalDisplay = convertedDisplay;
              let finalPrecise = convertedDecimal;
              
              if (newUnit === 'lbs') {
                if (convertedDisplay < 55) {
                  finalDisplay = 55;
                  finalPrecise = 55;
                } else if (convertedDisplay > 662) {
                  finalDisplay = 662;
                  finalPrecise = 662;
                }
              } else { // kg
                if (convertedDisplay < 25) {
                  finalDisplay = 25;
                  finalPrecise = 25;
                } else if (convertedDisplay > 300) {
                  finalDisplay = 300;
                  finalPrecise = 300;
                }
              }
              
              setWeight(finalDisplay.toString());
              setWeightPrecise(finalPrecise.toString());
            }
          }} value={unit} />

          <div className="age-container">
             <input
                ref={inputRef}
                enterKeyHint="done"
                className="height-label"
                placeholder={"Target Weight"}
                value={weight}
                onChange={handleWeightChange}
                onBlur={handleWeightBlurWithFloat}
                onFocus={handleFocus}
                onKeyDown={onPageKeyDown}
                readOnly={!isCurPage}
                type={"number"}
                inputMode="numeric"
                step="1"
            />

            <div className="unit-label">{unit}</div>
          </div>
          {isWeightValid(weight, unit) && <TargetWeightResult
              cur={data?.currentWeight}
              tar={new WeightModel(weight, unit)}
          />}
        </div>
      </div>
    </div>
  );
};
